import { useMemo, useState } from 'react';
import type { DocumentData } from 'firebase/firestore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { labelClassName } from '@/components/ui/label';
import { useT } from '@/lib/i18n';
import { fmtBytes, fmtDate } from '@/lib/utils';
import {
  EMPTY_SOURCE_FILTER, NO_FILTER, feasibilityTone, filterSources, parseDataCatalog,
  reachableTone, sensitivityTone, severityTone, sourceFilterOptions, sourceLabel, sourcesById,
  type SourceFilter,
} from '@/lib/catalog';
import {
  ArtifactLoader, Bullets, ContractTooOld, DataTable, Section, ToneBadge, contractLabel,
  useArtifactJson,
} from './CatalogShared';

/**
 * Onglet « Données » — catalogue consolidé du contrat 1.2
 * (`json/data_catalog.json`, sidecar du module 08).
 *
 * Ce que le client doit pouvoir lire ici : où sont ses données, dans quel
 * volume, à quel point elles sont fraîches, à qui elles appartiennent, ce
 * qu'il faudrait pour les brancher à une IA — et ce que l'audit n'a PAS pu
 * établir. Une mesure jamais faite s'affiche « non testé », jamais « bloqué »
 * ni « 0 ».
 */
export function DataCatalogTab({ auditId, module, schemaVersion }: {
  auditId?: string;
  module?: DocumentData;
  schemaVersion?: string | null;
}) {
  const { t, locale } = useT();
  const [filter, setFilter] = useState<SourceFilter>(EMPTY_SOURCE_FILTER);
  const artifact = useArtifactJson(auditId, module as { json?: unknown; jsonPath?: string } | undefined);
  const catalog = useMemo(() => parseDataCatalog(artifact.json), [artifact.json]);

  if (!catalog) {
    if (artifact.canLoad && artifact.path) {
      return (
        <ArtifactLoader
          path={artifact.path}
          label={t('audit.catalog.load')}
          loading={artifact.loading}
          error={artifact.error}
          onLoad={artifact.load}
        />
      );
    }
    return <ContractTooOld title={t('audit.catalog.unavailable.title')} schemaVersion={schemaVersion} />;
  }

  const options = sourceFilterOptions(catalog.sources);
  const shown = filterSources(catalog.sources, filter);
  const byId = sourcesById(catalog.sources);
  const filtered = filter !== EMPTY_SOURCE_FILTER
    && (filter.kind !== NO_FILTER || filter.engine !== NO_FILTER || filter.host !== NO_FILTER);
  const untested = t('audit.catalog.rows_untested');

  return (
    <div className="space-y-6">
      <Section
        title={t('audit.catalog.sources')}
        aside={
          <>
            <span>{t('audit.catalog.sources.count', { shown: shown.length, total: catalog.sources.length })}</span>
            {catalog.collectorHost && <span>{t('audit.catalog.collected', { host: catalog.collectorHost })}</span>}
          </>
        }
      >
        <div className="flex flex-wrap items-end gap-3">
          {(['kind', 'engine', 'host'] as const).map(field => (
            <label key={field} className="flex flex-col gap-1">
              <span className={labelClassName()}>{t(`audit.catalog.filter.${field}`)}</span>
              <select
                aria-label={t(`audit.catalog.filter.${field}`)}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm dark:border-ink-700 dark:bg-ink-800"
                value={filter[field]}
                onChange={e => setFilter({ ...filter, [field]: e.target.value })}
              >
                <option value={NO_FILTER}>{t('audit.catalog.filter.all')}</option>
                {options[field].map(v => (
                  <option key={v} value={v}>
                    {field === 'kind' ? contractLabel(t, 'audit.catalog.kind.' + v, v) : v}
                  </option>
                ))}
              </select>
            </label>
          ))}
          {filtered && (
            <Button variant="ghost" onClick={() => setFilter(EMPTY_SOURCE_FILTER)}>
              {t('audit.catalog.filter.reset')}
            </Button>
          )}
        </div>

        <DataTable
          empty={t('audit.catalog.no_match')}
          headers={[
            t('audit.catalog.col.source'), t('audit.catalog.col.kind'), t('audit.catalog.col.engine'),
            t('audit.catalog.col.host'), t('audit.catalog.col.reachable'),
            t('audit.catalog.col.hosting'), t('audit.catalog.col.discovery'),
          ]}
          rows={shown.map(s => [
            <span className="break-all" title={s.id}>{s.locator ?? sourceLabel(s)}</span>,
            contractLabel(t, 'audit.catalog.kind.' + s.kind, s.kind),
            s.engine ?? '—',
            // La provenance : qui HÉBERGE, et qui a OBSERVÉ (infobulle).
            <span title={s.observedBy ? `${s.observedBy}` : undefined}>{s.hostedBy ?? s.host ?? '—'}</span>,
            <ToneBadge tone={reachableTone(s.reachable)}>
              {s.reachable === null ? untested
                : t(s.reachable ? 'audit.catalog.reachable.yes' : 'audit.catalog.reachable.no')}
            </ToneBadge>,
            s.hosting ?? '—',
            <span title={s.discoveryEvidence ?? undefined}>
              {s.discoveryMethod ?? '—'}
              {s.confidence != null && <span className="text-ink-500"> · {s.confidence}</span>}
            </span>,
          ])}
        />
      </Section>

      <Section title={t('audit.catalog.datasets')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[
            t('audit.catalog.col.name'), t('audit.catalog.col.source'), t('audit.catalog.col.volume'),
            t('audit.catalog.col.freshness'), t('audit.catalog.col.domain'),
            t('audit.catalog.col.sensitivity'),
          ]}
          rows={catalog.datasets.map(d => [
            <span className="break-all" title={d.path ?? undefined}>{d.name}</span>,
            sourceLabel(byId.get(d.sourceId ?? '')),
            <span>
              {d.sizeBytes != null ? fmtBytes(d.sizeBytes) : '—'}
              {d.fileCount != null && (
                <span className="text-ink-500"> · {t('audit.catalog.files', { n: d.fileCount })}</span>
              )}
            </span>,
            d.lastModified ? fmtDate(d.lastModified, locale) : '—',
            d.businessDomain ?? '—',
            <>
              {d.ragPriority && (
                <ToneBadge tone="info">{contractLabel(t, 'audit.catalog.priority.' + d.ragPriority, d.ragPriority)}</ToneBadge>
              )}
            </>,
          ])}
        />
      </Section>

      <Section title={t('audit.catalog.connectors')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[
            t('audit.catalog.col.source'), t('audit.catalog.col.connector'),
            t('audit.catalog.col.feasibility'), t('audit.catalog.col.requirements'),
          ]}
          rows={catalog.connectors.map(c => [
            sourceLabel(byId.get(c.sourceId ?? '')),
            <span>
              {c.recommended ?? '—'}
              {c.incrementalStrategy && <span className="text-ink-500"> · {c.incrementalStrategy}</span>}
            </span>,
            <>
              <ToneBadge tone={feasibilityTone(c.feasibility)}>
                {contractLabel(t, 'audit.catalog.feasibility.' + c.feasibility, c.feasibility)}
              </ToneBadge>
              {c.networkPathOk === null && <div className="mt-1 text-xs text-ink-500">{untested}</div>}
            </>,
            <>
              <Bullets items={c.requirements} />
              {c.blockers.length > 0 && (
                <div className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                  <Bullets items={c.blockers} />
                </div>
              )}
            </>,
          ])}
        />
      </Section>

      <Section title={t('audit.catalog.access')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[
            t('audit.catalog.col.target'), t('audit.catalog.col.principals'),
            t('audit.catalog.col.rights'),
          ]}
          rows={catalog.access.map(a => [
            <span className="break-all">{a.target}</span>,
            <>
              {a.principals.map(pr => pr.name).join(', ') || '—'}
              {a.publicOrEveryone === true && (
                <div className="mt-1"><ToneBadge tone="danger">{t('audit.catalog.public')}</ToneBadge></div>
              )}
            </>,
            [...new Set(a.principals.flatMap(pr => pr.rights))].join(', ') || '—',
          ])}
        />
      </Section>

      <Section title={t('audit.catalog.sensitivity')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[
            t('audit.catalog.col.target'), t('audit.catalog.col.domain'),
            t('audit.catalog.col.sensitivity'), t('audit.catalog.col.pii'),
            t('audit.catalog.col.secrets'), t('audit.catalog.col.method'),
          ]}
          rows={catalog.sensitivity.map(e => [
            <span className="break-all">{e.targetPath}</span>,
            e.businessDomain ?? '—',
            <ToneBadge tone={sensitivityTone(e.level)}>
              {contractLabel(t, 'audit.catalog.level.' + e.level, e.level)}
            </ToneBadge>,
            formatCounters(e.piiTypes) ?? t('audit.catalog.pii.none'),
            formatCounters(e.secretTypes) ?? t('audit.catalog.pii.none'),
            contractLabel(t, 'audit.catalog.method.' + e.method, e.method),
          ])}
        />
      </Section>

      {catalog.scores.length > 0 && (
        <Section title={t('audit.catalog.scores')}>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {catalog.scores.map(sc => (
              <Card key={sc.name}>
                <div className={labelClassName()}>{sc.name}</div>
                <div className="mt-1 font-display text-lg text-ink-900 dark:text-white">
                  {sc.value == null ? t('audit.catalog.not_computable') : `${Math.round(sc.value * 100)} %`}
                </div>
                {sc.considered != null && (
                  <div className="text-xs text-ink-500">
                    {t('audit.catalog.basis', { satisfied: sc.satisfied ?? 0, considered: sc.considered })}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </Section>
      )}

      <Section title={t('audit.catalog.gaps')} description={t('audit.catalog.gaps.desc')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[t('audit.catalog.col.gap'), t('audit.catalog.col.detail'), t('audit.catalog.col.source')]}
          rows={catalog.gaps.map(g => [
            <ToneBadge tone={severityTone(g.severity)}>
              {contractLabel(t, 'audit.catalog.severity.' + g.severity, g.severity)}
            </ToneBadge>,
            <span>
              <code className="text-xs">{g.code}</code>
              <div>{g.message}</div>
            </span>,
            sourceLabel(byId.get(g.sourceId ?? '')),
          ])}
        />
      </Section>
    </div>
  );
}

/** `{email: 2, iban: 1}` → « email × 2 · iban × 1 » ; `null` si vide. */
function formatCounters(counters: Record<string, number>): string | null {
  const entries = Object.entries(counters);
  if (entries.length === 0) return null;
  return entries.map(([k, n]) => `${k} × ${n}`).join(' · ');
}
